/**
 * Cart Drawer Component
 * Handles the cart drawer functionality including package protection removal tracking
 */
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.packageProtectionJustRemoved = false;
    this.mutationObserver = null;

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
    this.initializeMutationObserver();
  }

  /**
   * Initialize MutationObserver to watch for DOM changes
   * Ensures package protection removal state persists through cart updates
   */
  initializeMutationObserver() {
    this.mutationObserver = new MutationObserver((mutations) => {
      if (this.packageProtectionJustRemoved) {
        this.updateCheckoutButtonForRemoval();
      }
    });

    this.mutationObserver.observe(this, {
      childList: true,
      subtree: true,
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);

        document
          .querySelector("cart-drawer-recommended")
          .fetchRecommendedProducts();
      },
      { once: true }
    );

    document.body.classList.add("overflow-x-hidden");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-x-hidden");

    // Reset package protection removal state when drawer closes
    this.packageProtectionJustRemoved = false;
    this.removeAttribute("data-protection-removed");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Apply package protection removal state before DOM update
    if (this.packageProtectionJustRemoved) {
      this.updateCheckoutButtonForRemoval();
    }

    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    // Reapply package protection removal state after DOM update
    if (this.packageProtectionJustRemoved) {
      this.updateCheckoutButtonForRemoval();
    }

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this)
      );
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  handlePackageProtectionRemoval(cartData) {
    const packageProtectionInCart = cartData.items.some(
      (item) =>
        item.product_type === "Package Protection" ||
        item.handle === "package-protection"
    );

    if (!packageProtectionInCart && !this.packageProtectionJustRemoved) {
      this.packageProtectionJustRemoved = true;
      this.updateCheckoutButtonForRemoval();
    }
  }

  /**
   * Sets data attribute to hide package protection elements via CSS
   */
  updateCheckoutButtonForRemoval() {
    this.setAttribute("data-protection-removed", "true");
  }
}

customElements.define("cart-drawer", CartDrawer);

/**
 * Cart Drawer Items Component
 * Manages cart item updates and package protection removal detection
 */
class CartDrawerItems extends CartItems {
  constructor() {
    super();
    this.previouslyHadProtection = false;
    this.initialized = false;
    this.setupRemoveButtonListener();
  }

  /**
   * Sets up event listener to detect package protection removal via remove button click
   * Uses event delegation for efficiency
   */
  setupRemoveButtonListener() {
    document.addEventListener("click", (event) => {
      const removeButton = event.target.closest(".cart-remove-button");
      if (!removeButton) return;

      const cartItem = removeButton.closest(".cart-item");
      if (!cartItem) return;

      const itemDetails = cartItem.querySelector(".cart-item__details");
      if (!itemDetails) return;

      const itemName = itemDetails.querySelector(".cart-item__name");
      if (!itemName) return;

      const itemNameText = itemName.textContent.toLowerCase();

      if (itemNameText.includes("package protection")) {
        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer) {
          cartDrawer.packageProtectionJustRemoved = true;
          cartDrawer.updateCheckoutButtonForRemoval();
        }
      }
    });
  }

  /**
   * Resets the component state to allow reinitialization
   * Called after back button navigation from checkout
   */
  resetState() {
    this.initialized = false;
    this.previouslyHadProtection = false;
  }

  onCartUpdate() {
    const cartDrawer = document.querySelector("cart-drawer");

    if (!this.initialized) {
      this.initializeProtectionState();
      super.onCartUpdate();
      return;
    }

    // Check for package protection removal before DOM update
    this.checkPackageProtectionRemovalSync();

    // Call parent's update method
    super.onCartUpdate();

    // Reapply removal state if needed after DOM update
    if (cartDrawer && cartDrawer.packageProtectionJustRemoved) {
      cartDrawer.updateCheckoutButtonForRemoval();
    }
  }

  /**
   * Synchronously checks if package protection was removed from cart
   * Compares previous state with current DOM to detect removal
   */
  checkPackageProtectionRemovalSync() {
    const cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer) return;

    const form = document.getElementById("CartDrawer-Form");
    if (!form) return;

    const cartItems = form.querySelectorAll(".cart-item");
    let packageProtectionInCart = false;

    cartItems.forEach((item) => {
      const itemDetails = item.querySelector(".cart-item__details");
      if (itemDetails) {
        const itemName = itemDetails.querySelector(".cart-item__name");
        if (itemName) {
          const itemNameText = itemName.textContent.toLowerCase();
          if (itemNameText.includes("package protection")) {
            packageProtectionInCart = true;
          }
        }
      }
    });

    // Detect removal: went from present to absent
    if (this.previouslyHadProtection && !packageProtectionInCart) {
      cartDrawer.packageProtectionJustRemoved = true;
      cartDrawer.updateCheckoutButtonForRemoval();
    } else if (packageProtectionInCart) {
      // Reset if package protection is back in cart
      cartDrawer.packageProtectionJustRemoved = false;
      cartDrawer.removeAttribute("data-protection-removed");
      this.previouslyHadProtection = true;
      return;
    }

    this.previouslyHadProtection = packageProtectionInCart;
  }

  /**
   * Initializes the package protection state by fetching current cart data
   */
  initializeProtectionState() {
    const cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer) return;

    fetch(window.Shopify.routes.root + "cart.js")
      .then((response) => response.json())
      .then((cartData) => {
        this.previouslyHadProtection = cartData.items.some(
          (item) =>
            item.product_type === "Package Protection" ||
            item.handle === "package-protection"
        );
        this.initialized = true;
      })
      .catch((error) => {
        console.error("Error initializing package protection state:", error);
        this.initialized = true;
      });
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

/**
 * Cart Drawer Recommended Products Component
 * Fetches and displays product recommendations in the cart drawer
 */
class CartDrawerRecommended extends HTMLElement {
  constructor() {
    super();
  }

  fetchRecommendedProducts() {
    const productId = this?.getAttribute("data-product-ids");
    if (!productId) return;

    const url = `/recommendations/products?product_id=${productId}&limit=4&section_id=aa-product-recommendations`;
    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, "text/html");
        const recommendations = html.querySelector("product-recommendations");
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.replaceChildren(recommendations);
        }
      })
      .catch((error) =>
        console.error("Error fetching recommended products:", error)
      );
  }
}

customElements.define("cart-drawer-recommended", CartDrawerRecommended);

/**
 * Package Protection Handler
 * Manages package protection addition during checkout
 */
class PackageProtectionHandler {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener(
      "click",
      this.handleCheckoutClick.bind(this),
      true
    );
    document.addEventListener("submit", this.handleFormSubmit.bind(this), true);
  }

  handleFormSubmit(event) {
    const form = event.target;
    if (form.id !== "CartDrawer-Form") return;

    const checkoutButton = form.querySelector(
      "#CartDrawer-Checkout[data-add-protection]"
    );
    if (!checkoutButton) return;

    event.preventDefault();
    event.stopPropagation();

    const protectionId = checkoutButton.getAttribute("data-protection-id");
    if (!protectionId) {
      window.location.href = "/checkout";
      return;
    }

    this.addPackageProtection(protectionId);
  }

  handleCheckoutClick(event) {
    const checkoutButton = event.target.closest("#CartDrawer-Checkout");
    if (!checkoutButton) return;

    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer && cartDrawer.packageProtectionJustRemoved) {
      // Go directly to checkout without adding protection if it was just removed
      event.preventDefault();
      event.stopPropagation();
      window.location.href = "/checkout";
      return;
    }

    if (!checkoutButton.hasAttribute("data-add-protection")) return;

    event.preventDefault();
    event.stopPropagation();

    const protectionId = checkoutButton.getAttribute("data-protection-id");
    if (!protectionId) {
      window.location.href = "/checkout";
      return;
    }

    this.addPackageProtection(protectionId);
  }

  addPackageProtection(variantId) {
    const formData = {
      items: [
        {
          id: parseInt(variantId),
          quantity: 1,
        },
      ],
    };

    fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    })
      .then((response) => response.json())
      .then((data) => {
        window.location.href = "/checkout";
      })
      .catch((error) => {
        console.error("Error adding package protection:", error);
        window.location.href = "/checkout";
      });
  }
}

document.addEventListener("DOMContentLoaded", function () {
  new PackageProtectionHandler();
});

/**
 * Handle back button navigation from checkout
 * Resets cart state and refreshes content when returning from checkout via back button
 */
window.addEventListener("pageshow", function (event) {
  if (event.persisted) {
    const cartDrawerItems = document.querySelector("cart-drawer-items");
    if (cartDrawerItems) {
      cartDrawerItems.resetState();
    }

    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer) {
      cartDrawer.packageProtectionJustRemoved = false;
      cartDrawer.removeAttribute("data-protection-removed");
    }

    fetch(window.Shopify.routes.root + "?sections=cart-drawer,cart-icon-bubble")
      .then((response) => response.json())
      .then((data) => {
        if (cartDrawer && data["cart-drawer"]) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(data["cart-drawer"], "text/html");
          const newDrawerInner = doc.querySelector(".drawer__inner");

          if (newDrawerInner) {
            const currentDrawerInner =
              cartDrawer.querySelector(".drawer__inner");
            if (currentDrawerInner) {
              currentDrawerInner.replaceWith(newDrawerInner);
            }
          }
        }

        if (data["cart-icon-bubble"]) {
          const cartIconBubble = document.getElementById("cart-icon-bubble");
          if (cartIconBubble) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(
              data["cart-icon-bubble"],
              "text/html"
            );
            const newBubble = doc.querySelector(".shopify-section");
            if (newBubble) {
              cartIconBubble.innerHTML = newBubble.innerHTML;
            }
          }
        }
      })
      .catch((error) => {
        console.error("Error refreshing cart on back navigation:", error);
      });
  }
});
